import { default as axios } from './axiosConfig'
import { ProviderSettingsType } from '@dataTypes/procedureSettings.type'
import { Provider, ProviderData } from '@dataTypes/provider.types'
import {
  mapProviderDataToDatabase,
  mapPartialProviderDataToDatabase,
  removeEmptyValues,
} from '@utils/provider.mapper'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'

// Define the types for the response structure
export type AuthRequestBody = {
  start_date: string
}

// Generic API response type
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

// Specific response types for better type safety
export type ProvidersResponse = ApiResponse<{ providers: Provider[] }>
export type StaffsResponse = ApiResponse<{ staffs: Provider[] }>
export type ProviderSeatLimitResponse = ApiResponse<{ provider_limit: number }>
export type ProviderResponse = ApiResponse<Provider>
export type CreateProviderApiResponse = ApiResponse<{
  provider: Provider
}>
export type UpdateProviderPhotoByIdResponse = ApiResponse<{
  provider: Provider
}>
export type SearchProvidersByNameResponse = ApiResponse<{
  providers: Provider[]
}>
export type ProviderSettingsResponse = ApiResponse<ProviderSettingsType>

export type StaffEmailApiData = {
  ok: boolean
  skipped?: boolean
  message?: string
}

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_PROVIDER')

const headers = {
  'Content-Type': 'application/json',
}

// Function to get all providers
export const getAllProviders = async (
  clinicId?: string,
  options?: { accessToken?: string },
): Promise<ProvidersResponse> => {
  try {
    const { data, status } = await axios.get<ProvidersResponse>(
      `${BUSINESS_LAYER_API}/api/v1/provider`,
      {
        headers: {
          ...headers,
          ...(options?.accessToken
            ? { Authorization: `Bearer ${options.accessToken}` }
            : {}),
          ...(clinicId ? { 'X-Clinic-ID': clinicId } : {}),
        },
        params: clinicId ? { clinic_id: clinicId } : undefined,
      },
    )

    // Check if the response status is 200, throw an error otherwise
    if (status !== 200) {
      throw new Error('Failed to fetch providers')
    }
    return data
  } catch (e) {
    logger.error('Error fetching providers:', e)
    throw toApiError(e, 'Error fetching providers')
  }
}

export const getAllStaffs = async (): Promise<StaffsResponse> => {
  try {
    const { data, status } = await axios.get<StaffsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/provider/staffs`,
      {
        headers,
      },
    )

    if (status !== 200) {
      throw new Error('Failed to fetch staff list')
    }
    return data
  } catch (e) {
    logger.error('Error fetching staff list:', e)
    throw toApiError(e, 'Error fetching staff')
  }
}

export const getProviderSeatLimit =
  async (): Promise<ProviderSeatLimitResponse> => {
    try {
      const { data, status } = await axios.get<ProviderSeatLimitResponse>(
        `${BUSINESS_LAYER_API}/api/v1/provider/settings/provider-limit`,
        { headers },
      )
      if (status !== 200) {
        throw new Error('Failed to fetch provider seat limit')
      }
      return data
    } catch (e) {
      logger.error('Error fetching provider seat limit:', e)
      throw toApiError(e, 'Error fetching provider seat limit')
    }
  }

export const sendStaffEmail = async (payload: {
  email: string
  firstName: string
  lastName: string
  username: string
  providerId?: string
}): Promise<void> => {
  try {
    await axios.post<ApiResponse<StaffEmailApiData>>(
      `${BUSINESS_LAYER_API}/api/v1/provider/settings/staff-email`,
      payload,
      { headers },
    )
  } catch (e) {
    logger.error('sendStaffEmail failed', e)
  }
}

export const createProvider = async (
  provider: ProviderData,
): Promise<CreateProviderApiResponse> => {
  try {
    const databaseProviderData = removeEmptyValues(
      mapProviderDataToDatabase(provider),
    )
    const { data, status } = await axios.post<CreateProviderApiResponse>(
      `${BUSINESS_LAYER_API}/api/v1/provider`,
      databaseProviderData,
      {
        headers,
      },
    )

    // Check if the response status is 201, throw an error otherwise
    if (status !== 201) {
      throw new Error('Failed to create provider')
    }
    return data
  } catch (e) {
    logger.error('Error creating provider:', e)
    throw toApiError(e, 'Error creating provider')
  }
}

export const getProvider = async (
  provider_id: string,
): Promise<ProviderResponse> => {
  try {
    const { data, status } = await axios.get<ProviderResponse>(
      `${BUSINESS_LAYER_API}/api/v1/provider/${provider_id}`,
      { headers },
    )

    if (status !== 200) {
      throw new Error('Failed to fetch provider')
    }
    return data
  } catch (e) {
    logger.error('Error fetching provider:', e)
    throw toApiError(e, 'Error fetching provider')
  }
}

/** Normalize business-layer envelope to a Provider row. */
export const parseRow = (res: ProviderResponse): Provider | null => {
  if (res.statusCode !== 200) return null
  const raw = res.data as unknown
  if (!raw || typeof raw !== 'object') return null
  if ('provider' in raw && (raw as { provider: Provider }).provider) {
    return (raw as { provider: Provider }).provider
  }
  if ('provider_id' in raw) return raw as Provider
  return null
}

export const loadRow = async (
  provider_id: string,
): Promise<Provider | null> => {
  const res = await getProvider(provider_id)
  return parseRow(res)
}

export const updateProviderById = async (
  provider_id: string,
  provider: Partial<ProviderData>,
): Promise<ProviderResponse> => {
  try {
    // PUT sends only changed fields (delta); map partial form data to database shape
    const data = mapPartialProviderDataToDatabase(provider)
    const { data: responseData, status } = await axios.put<ProviderResponse>(
      `${BUSINESS_LAYER_API}/api/v1/provider`,
      {
        provider_id,
        data,
      },
      {
        headers,
      },
    )

    if (status !== 200) {
      throw new Error('Failed to update provider')
    }
    return responseData
  } catch (e) {
    logger.error('Error updating provider:', e)
    throw toApiError(e, 'Error updating provider')
  }
}

// Same payload as PUT; backend should merge partial `data` only.
export const patchProvider = async (
  provider_id: string,
  provider: Partial<ProviderData>,
): Promise<ProviderResponse> => {
  try {
    const data = mapPartialProviderDataToDatabase(provider)
    const { data: responseData, status } = await axios.patch<ProviderResponse>(
      `${BUSINESS_LAYER_API}/api/v1/provider`,
      {
        provider_id,
        data,
      },
      { headers },
    )

    if (status !== 200) {
      throw new Error('Failed to patch provider')
    }
    return responseData
  } catch (e) {
    logger.error('Error patching provider:', e)
    throw toApiError(e, 'Error patching provider')
  }
}

export const deleteProviderById = async (
  provider_id: string,
): Promise<ProviderResponse> => {
  try {
    const { data, status } = await axios.delete<ProviderResponse>(
      `${BUSINESS_LAYER_API}/api/v1/provider/${provider_id}`,
      { headers },
    )

    if (status !== 200) {
      throw new Error('Failed to delete provider')
    }
    return data
  } catch (e) {
    logger.error('Error deleting provider:', e)
    throw toApiError(e, 'Error deleting provider')
  }
}

export const searchProvidersByName = async (
  name: string,
  clinicId?: string,
  options?: { accessToken?: string },
): Promise<SearchProvidersByNameResponse> => {
  try {
    const { data, status } = await axios.get<SearchProvidersByNameResponse>(
      `${BUSINESS_LAYER_API}/api/v1/provider/search`,
      {
        headers: {
          ...headers,
          ...(options?.accessToken
            ? { Authorization: `Bearer ${options.accessToken}` }
            : {}),
          ...(clinicId ? { 'X-Clinic-ID': clinicId } : {}),
        },
        params: {
          name,
          ...(clinicId ? { clinic_id: clinicId } : {}),
        },
      },
    )

    // Check if the response status is 200, throw an error otherwise
    if (status !== 200) {
      throw new Error('Failed to fetch providers')
    }

    return data
  } catch (e) {
    logger.error('Error fetching providers:', e)
    throw toApiError(e, 'Error fetching providers')
  }
}

// Function to get provider settings
export const getProviderSettings =
  async (): Promise<ProviderSettingsResponse> => {
    try {
      const { data, status } = await axios.get<ProviderSettingsResponse>(
        `${BUSINESS_LAYER_API}/api/v1/providersettings`,
        {
          headers,
        },
      )

      // Check if the response status is 200, throw an error otherwise
      if (status !== 200) {
        throw new Error('Failed to fetch provider settings')
      }
      return data
    } catch (e) {
      logger.error('Error fetching provider settings:', e)
      throw toApiError(e, 'Error fetching provider settings')
    }
  }

export const addOrUpdateProviderSettings = async (
  providerSettings: Partial<ProviderSettingsType>,
): Promise<ProviderSettingsResponse> => {
  try {
    const { data, status } = await axios.post<ProviderSettingsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/providersettings`,
      providerSettings,
      {
        headers,
      },
    )

    // Check if the response status is 201, throw an error otherwise
    if (status !== 201) {
      throw new Error('Failed to save provider settings')
    }
    return data
  } catch (e) {
    logger.error('Error saving provider settings:', e)
    throw toApiError(e, 'Error saving provider settings')
  }
}

export const updateProviderPhotoById = async (
  provider_id: string,
  file: File,
): Promise<UpdateProviderPhotoByIdResponse> => {
  try {
    const formData = new FormData()
    formData.append('files', file, file.name)
    const response = await axios.put<UpdateProviderPhotoByIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/provider/${provider_id}/photo`,
      formData,
      {
        headers: {
          'Content-Type': undefined,
        },
      },
    )

    return response.data
  } catch (e) {
    logger.error('Error in updateProviderPhotoById:', e)
    throw toApiError(e, 'Error updating provider photo')
  }
}
